/**
 * "May this SKU be keyed in fractions?" — the screen-side copy of the backend
 * rule in `backend/src/common/quantity/quantity.util.ts`. Keep the two in step.
 *
 * A pack of biscuits is counted (2 pcs, never 2.5); atta out of a 50 kg sack is
 * weighed (2.5 kg is the normal case). PO, GRN, PRN and Transfer Out all ask
 * this before deciding what a quantity box accepts.
 *
 * The server enforces the same rule on save — this is the courtesy that stops a
 * bad keystroke, not the guarantee.
 */

/** Quantities are stored as DECIMAL(18,3) — three places, no more. */
export const QUANTITY_DECIMALS = 3;

/**
 * UOM codes that measure rather than count. Anything not listed — PCS, NOS,
 * PKT, BOX, BAG, CTN, DOZ … — is counted and stays whole.
 */
const FRACTIONAL_UOMS = new Set([
  // mass
  'KG', 'KGS', 'KILO', 'KILOS', 'KILOGRAM', 'KILOGRAMS', 'KILOGRAMME',
  'G', 'GM', 'GMS', 'GRAM', 'GRAMS', 'GRM', 'GRMS',
  'MG', 'QTL', 'QUINTAL', 'TON', 'TONS', 'TONNE', 'TONNES', 'MT',
  // volume
  'L', 'LT', 'LTR', 'LTRS', 'LITRE', 'LITRES', 'LITER', 'LITERS',
  'ML', 'MLS', 'MILLILITRE', 'MILLILITER', 'CL', 'KL',
  // length / area
  'M', 'MTR', 'MTRS', 'METER', 'METERS', 'METRE', 'METRES',
  'CM', 'MM', 'FT', 'FEET', 'INCH', 'SQM', 'SQFT',
]);

/** Anything a document line or a product row might carry the flags on. */
export interface QuantityFlags {
  uom?: string | null;
  baseUom?: string | null;
  isBulk?: boolean | null;
  isWeighable?: boolean | null;
  bulkParentProductId?: string | null;
  /** Set by the Transfer Out upload response, which resolves this server-side. */
  allowsFraction?: boolean | null;
  /** Lines often nest the product/variant they were built from. */
  product?: QuantityFlags | null;
  productVariant?: { product?: QuantityFlags | null } | null;
  /** The PO screen keeps the whole product row on the line under this name. */
  productData?: QuantityFlags | null;
}

export function normaliseUom(uom?: string | null): string {
  return String(uom ?? '').toUpperCase().replace(/[\s.]/g, '');
}

export function isFractionalUom(uom?: string | null): boolean {
  const key = normaliseUom(uom);
  return key.length > 0 && FRACTIONAL_UOMS.has(key);
}

/**
 * Decimals are allowed when EITHER the unit measures (kg, gm, ltr …) OR the
 * product is flagged bulk / weighable / a bulk child. Both are checked because
 * older SKUs predate the flags, and some sacks carry uom = 'PCS' even though
 * they are broken down by weight.
 *
 * Nested shapes (`item.product`, `item.productVariant.product`) are searched
 * too, since the purchase screens build their lines from several sources.
 */
export function allowsDecimalQty(source: QuantityFlags | null | undefined): boolean {
  if (!source) return false;
  if (source.allowsFraction === true) return true;
  if (source.isBulk === true || source.isWeighable === true) return true;
  if (source.bulkParentProductId) return true;
  if (isFractionalUom(source.uom) || isFractionalUom(source.baseUom)) return true;
  if (source.product && allowsDecimalQty(source.product)) return true;
  if (source.productVariant?.product && allowsDecimalQty(source.productVariant.product)) {
    return true;
  }
  if (source.productData && allowsDecimalQty(source.productData)) return true;
  return false;
}

/** Round to the stored precision; junk collapses to 0. */
export function roundQty(value: unknown, decimals = QUANTITY_DECIMALS): number {
  const n = Number(value);
  if (!isFinite(n)) return 0;
  const f = Math.pow(10, decimals);
  return Math.round(n * f) / f;
}

/**
 * Normalise a keyed/pasted quantity to what the SKU actually accepts: whole
 * numbers for counted items, up to three decimals for measured ones.
 */
export function normaliseQty(value: unknown, allowDecimal: boolean): number {
  const n = Number(value);
  if (!isFinite(n)) return 0;
  return allowDecimal ? roundQty(n) : Math.round(n);
}
